import React, { useCallback, useEffect, useState, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import dayjs from "dayjs";
import advancedFormat from "dayjs/plugin/advancedFormat";
import { useRouter } from "next/router";
import {
  deleteData,
  getData,
  patchData,
  postData,
} from "../../../../src/utilities/axios";
import {
  Avatar,
  Spinner,
  Text,
  Switch,
  useDisclosure,
  useToast,
} from "@chakra-ui/react";
import withAuth from "../../../../src/hooks/withAuth";
import { useStripeCustomer } from "../../../../src/hooks/useStripeCustomer";
import { UserRegisterSuccessModal } from "../../../../src/components/modals/UserRegisterSuccessModal";
import {
  PiUserCircle,
  PiChatCircle,
  PiCalendar,
  PiCalendarBlank,
  PiArrowRight,
  PiGear,
  PiEnvelopeSimple,
} from "react-icons/pi";
import { Button } from "@headlessui/react";
import { VscMegaphone } from "react-icons/vsc";
import * as N from "../../../../src/utilities/notifications";
import Link from "next/link";
import Image from "next/image";
import { FaBookmark } from "react-icons/fa6";
import { TiDocumentText } from "react-icons/ti";
import { MdInsertChartOutlined } from "react-icons/md";
import useStripeConnect from "../../../../src/hooks/useStripeConnect";
import {
  ComposedChart,
  Line,
  Area,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
} from "recharts";
import { ViewsChartTooltip } from "../../../../src/components/tooltips/ViewsChartTooltip";
import { ChangeDashboardViewModal } from "../../../../src/components/modals/ChangeDashboardViewModal";
import { NoticeItem } from "../../../../src/components/common/NoticeItem";
import { AnimatePresence } from "framer-motion";
import { IncompleteProfileCard } from "../../../../src/accounts/practitioner-account/dashboard/IncompleteProfileCard";
import StoryblokClient from "storyblok-js-client";
import { updateUserDetails } from "../../../../src/redux/actions/user";
import CustomAvatar from "../../../../src/components/common/CustomAvatar";
import ProfileCompletion from "../../../../src/accounts/practitioner-account/editProfiles/common-screens/ProfileCompletion";

dayjs.extend(advancedFormat);

const Storyblok = new StoryblokClient({
  accessToken: process.env.NEXT_PUBLIC_STORYBLOK_KEY,
  region: "eu",
});

const initialNoticeDisplayCount = 3;

const TherapistDashboard = () => {
  const router = useRouter();
  const toast = useToast();
  const dispatch = useDispatch();
  const { success } = router.query;

  const token = useSelector((state) => state.user.JWTToken);
  const user = useSelector((state) => state.user.userDetails);
  const savedArticleIds = useSelector((state) => state.bookmarks.articles);
  const profile = useSelector(
    (state) => state?.user?.userDetails?.therapistProfile,
  );

  console.log("dashboard profile", profile);

  const [customer] = useStripeCustomer(user.stripeCustomerId);
  const [_, isOnboarded, stripeLoaded] = useStripeConnect(
    user.stripeConnectedAccount,
  );

  const [dashboardData, setDashboardData] = useState(null);
  const [hasInitialised, setHasInitialised] = useState(false);
  const [viewingContact, setViewContact] = useState(false);
  const [expandNotices, setExpandNotices] = useState(false);
  const [savedTherapists, setSavedTherapists] = useState([]);
  const [deletingList, setDeletingList] = useState([]);
  const [loadingTherapists, setLoadingTherapists] = useState(false);
  const [savedArticles, setSavedArticles] = useState([]);
  const [loadingArticles, setLoadingArticles] = useState(true);
  const [deletingArticleList, setDeletingArticleList] = useState([]);
  const [dataView, setDataView] = useState("profile_view");

  useEffect(() => {
    if (savedArticleIds.length > 0) {
      setLoadingArticles(true);
      Storyblok.get(`cdn/stories`, {
        by_uuids: savedArticleIds.toString(),
      })
        .then(({ data: { stories } }) => {
          setSavedArticles(stories);
          setLoadingArticles(false);
        })
        .catch((error) => {
          console.error(error);
        });
    } else {
      setLoadingArticles(false);
    }
  }, [savedArticleIds]);

  const {
    isOpen: isSuccessOpen,
    onClose: onSuccessClose,
    onOpen: onSuccessOpen,
  } = useDisclosure();

  const {
    isOpen: changingHome,
    onClose: closeChangingHome,
    onOpen: openChangingHome,
  } = useDisclosure();

  const getDashboardData = useCallback(async () => {
    if (!token) {
      return null;
    }
    setDashboardData(null);

    const data = await getData(
      `${process.env.NEXT_PUBLIC_NEW_USER_URL}dashboard/therapist`,
      null,
      token,
    );

    setDashboardData(data);
  }, [token, setDashboardData]);

  const removeSavedArticle = async (articleId) => {
    try {
      setDeletingArticleList([...deletingArticleList, articleId]);
      await postData(
        token,
        {},
        `${process.env.NEXT_PUBLIC_NEW_USER_URL}articleBookmark/${articleId}/remove`,
        process.env.NEXT_PUBLIC_NEW_USER_APIKEY,
      );

      setSavedArticles(
        [...savedArticles].filter((item) => item.uuid !== articleId),
      );
      setDeletingList(
        [...deletingList].filter((item) => item.uuid !== articleId),
      );
    } catch (error) {
      console.error(error);
      toast(N.genericFail("Problem removing saved article"));
      setDeletingList(
        [...deletingList].filter((item) => item.uuid !== articleId),
      );
    }
  };

  const getSavedTherapists = useCallback(async () => {
    try {
      if (loadingTherapists) {
        return;
      }
      setLoadingTherapists(true);

      const res = await getData(
        `${process.env.NEXT_PUBLIC_NEW_SEARCH_URL}save`,
        process.env.NEXT_PUBLIC_NEW_SEARCH_APIKEY,
        token,
      );

      if (res.length > 5) {
        setSavedTherapists(res.slice(0, 5));
      } else {
        setSavedTherapists(res);
      }
      setLoadingTherapists(false);
    } catch (error) {
      setLoadingTherapists(false);
    } finally {
      setLoadingTherapists(false);
    }
  }, [token, loadingTherapists, toast]);

  const removeSavedTherapist = async (therapistId) => {
    try {
      setDeletingList([...deletingList, therapistId]);

      await deleteData(
        token,
        {
          profileId: therapistId,
          practitionerType: "therapist",
        },
        `${process.env.NEXT_PUBLIC_NEW_SEARCH_URL}save`,
        process.env.NEXT_PUBLIC_NEW_SEARCH_APIKEY,
      );
      await getSavedTherapists();
    } catch (error) {
      toast(N.genericFail("Problem removing saved therapist"));
      setDeletingList([...deletingList].filter((item) => item !== therapistId));
    }
  };

  useEffect(() => {
    if (!token || hasInitialised) {
      return;
    }

    if (success === "true") {
      onSuccessOpen();
    }

    setHasInitialised(true);
    getDashboardData();
    getSavedTherapists();
  }, [
    getSavedTherapists,
    getDashboardData,
    hasInitialised,
    onSuccessOpen,
    success,
    token,
  ]);

  const chartData = useMemo(() => {
    if (!dashboardData || !dashboardData.views) {
      return [];
    }

    const data = {};
    for (const item of dashboardData.views) {
      data[item.view_month] = {
        ...data[item.view_month],
        [item.viewType === 2 ? "search" : "profile_view"]: item.view_count,
      };
    }

    return Object.keys(data).map((key) => ({
      name: dayjs(key).format("MMM YY"),
      profile_view: data[key].profile_view ?? 0,
      search: data[key].search ?? 0,
    }));
  }, [dashboardData]);

  const nextBooking = useMemo(() => {
    if (!dashboardData) {
      return "No bookings scheduled";
    }

    if (dashboardData.upcomingBookings.length === 0) {
      return "No bookings scheduled";
    }

    return dayjs(dashboardData.upcomingBookings[0].bookingDate).format(
      "Do MMM YYYY HH:mm",
    );
  }, [dashboardData]);

  const profileComplete = useMemo(() => {
    if (!profile) {
      return false;
    }

    return profile.profileComplete;
  }, [profile]);

  const verificationPaid = useMemo(() => {
    if (!profile) {
      return false;
    }

    return dayjs(profile.verificationPaidAt) > dayjs().subtract(1, "y");
  }, [profile]);

  const isInvalidMembership = useMemo(() => {
    return (
      !token ||
      user.accountType !== 2 ||
      !profile.paymentPlan ||
      profile.membershipStatus === "CANCELLED"
    );
  }, [token, user, profile]);

  useEffect(() => {
    if (user.firstLogin && user.accountType === 2) {
      const updateUser = async () => {
        try {
          await patchData(
            token,
            {
              profileId: user.id,
              practitionerType: "therapist",
              firstLogin: false,
            },
            `${process.env.NEXT_PUBLIC_NEW_USER_URL}profile`,
          );
          await dispatch(
            updateUserDetails({
              firstLogin: false,
            }),
          );
          if (!verificationPaid) {
            router.push(
              "/accounts/common-account/account-settings?tab=verification",
            );
          }
        } catch (error) {
          console.error("PROBLEM UPDATING FIRST TIME LOGIN", error);
          toast(N.updateDetailsFail(error));
        }
      };
      updateUser();
    }
  }, [user]);

  const toggleDataView = (dataKey) => {
    setDataView(dataKey);
  };

  return (
    <div className="flex flex-col w-screen items-center">
      <div className="bg-brand-alt flex justify-between items-end w-full">
        <div className="max-w-[1148px] py-4 sm:py-0 w-full mx-auto flex justify-between items-start md:items-center px-6 flex-col md:flex-row">
          <div className="flex flex-col py-2 md:py-8">
            <Text fontSize="md" color="white" className="font-light">
              {dayjs().format("dddd Do, MMMM")}
            </Text>
            <Text fontSize="3xl" color="white" className="font-bold">
              Welcome {user.fullname}
            </Text>
          </div>
          <Button
            onClick={openChangingHome}
            className="rounded-xl border border-white text-white px-4 py-2 bg-brand-alt transition hover:bg-white hover:text-brand-alt"
          >
            Switch to User view
          </Button>
        </div>
      </div>
      <div className="flex-1 flex flex-col w-full bg-[#F2F1EF] items-center pb-6">
        <div className="w-full max-w-[1148px] pt-6 flex flex-col gap-6 px-6">
          {profile?.outOfOffice && (
            <ProfileCompletion
              profile={profile}
              onView={() => setTabIndex(2)}
            />
          )}
          <div>
            <IncompleteProfileCard
              paymentPlan={profile.paymentPlan}
              profileComplete={profileComplete}
              hasPaymentMethod={
                user.isGoCardless === 1 || customer?.hasPaymentMethod
              }
              verificationPaid={verificationPaid}
              isOnboarded={isOnboarded}
              loading={
                (user.isGoCardless === 0 && !customer) ||
                !stripeLoaded ||
                !profile
              }
            />
          </div>

          <Link
            prefetch={false}
            href="/accounts/practitioner-account/dashboard/bookings"
            className="flex flex-col gap-4 dashboard-card hover:bg-brand-off cursor-pointer py-4 md:py-6"
          >
            <div className="flex items-center gap-3 text-brand-text border-b w-full pb-4">
              <PiCalendar className="h-6 w-6 mr-2" />
              <span className="font-bold text-lg">Bookings and enquiries</span>
            </div>
            <div className="md:gap-4 flex flex-col divide-y sm:divide-y-0 sm:flex-row">
              <div className="flex items-center sm:flex-col flex-1 gap-4 py-2 sm:py-0">
                <div className="flex flex-1 text-brand items-center">
                  <PiEnvelopeSimple className="h-6 w-6 mr-2" />
                  <span className="font-bold">Total contacts</span>
                </div>
                {dashboardData ? (
                  <span className="text-lg font-bold">
                    {dashboardData.contacts}
                  </span>
                ) : (
                  <div className="py-2">
                    <Spinner color="#587500" size="md" />
                  </div>
                )}
              </div>
              <div className="flex items-center sm:flex-col flex-1 gap-4 py-2 sm:py-0">
                <div className="flex flex-1 text-brand items-center">
                  <PiCalendar className="h-6 w-6 mr-2" />
                  <span className="font-bold">Next booking</span>
                </div>
                {dashboardData ? (
                  <span className="text-lg sm:text-lg font-bold text-right">
                    {nextBooking}
                  </span>
                ) : (
                  <div className="py-2">
                    <Spinner color="#587500" size="md" />
                  </div>
                )}
              </div>
              <div className="flex items-center sm:flex-col flex-1 gap-4 py-2 sm:py-0">
                <div className="flex flex-1 text-brand items-center">
                  <PiChatCircle className="h-6 w-6 mr-2" />
                  <span className="font-bold">Unread chats</span>
                </div>
                {dashboardData ? (
                  <span className="text-lg sm:text-lg font-bold">
                    {dashboardData ? dashboardData.unreadThreads.length : 0}
                  </span>
                ) : (
                  <div className="py-2">
                    <Spinner color="#587500" size="md" />
                  </div>
                )}
              </div>
              <div className="h-full flex items-center justify-end sm:self-center py-2 pt-4 sm:pt-0 sm:py-0">
                <div className="h-6 w-6 sm:w-10 sm:h-10 bg-brand rounded-full flex items-center justify-center text-white">
                  <PiArrowRight className="h-5 w-5 sm:h-8 sm:w-8" />
                </div>
              </div>
            </div>
          </Link>

          <div className="flex flex-col-reverse md:grid grid-cols-3 gap-6">
            <Button
              onClick={() => setViewContact(!viewingContact)}
              className="dashboard-card flex-row items-center hover:bg-brand-off cursor-pointer gap-5 md:hidden"
            >
              <div className="flex flex-col gap-2 flex-1">
                <div className="flex items-center gap-3 text-brand-text">
                  <PiEnvelopeSimple className="h-6 w-6" />
                  <span className="font-bold text-lg text-left">
                    Contact Welldoing
                  </span>
                </div>
                {viewingContact ? (
                  <div className="flex flex-col divide-y">
                    <div className="flex items-center gap-2 py-2">
                      <PiEnvelopeSimple className="h-4 w-4" />
                      <span className="font-bold text-brand-text">Email: </span>
                      <Link
                        prefetch={false}
                        onClick={(e) => e.stopPropagation()}
                        href="mailto:info@welldoing.org"
                      >
                        <span className="text-brand underline">
                          info@welldoing.org
                        </span>
                      </Link>
                    </div>
                  </div>
                ) : (
                  <span className="text-left font-light text-sm">
                    Welldoing is here to support you every step of the way.
                  </span>
                )}
              </div>
              <div
                className={`h-full flex items-center ${viewingContact ? "hidden" : ""}`}
              >
                <div className="w-10 h-10 bg-brand rounded-full flex items-center justify-center text-white">
                  <PiArrowRight className="h-8 w-8" />
                </div>
              </div>
            </Button>

            <Link
              prefetch={false}
              href="/accounts/common-account/account-settings"
              className="md:hidden"
            >
              <div className="dashboard-card flex-row items-center hover:bg-brand-off cursor-pointer gap-5">
                <div className="flex flex-col gap-2 flex-1">
                  <div className="flex items-center gap-3 text-brand-text">
                    <PiGear className="h-6 w-6" />
                    <span className="font-bold text-lg">Account settings</span>
                  </div>
                  <span className="text-left font-light text-sm">
                    Manage your account and payments
                  </span>
                </div>
                <div className="h-full flex items-center self-center">
                  <div className="w-10 h-10 bg-brand rounded-full flex items-center justify-center text-white">
                    <PiArrowRight className="h-8 w-8" />
                  </div>
                </div>
              </div>
            </Link>

            <Link
              prefetch={false}
              href="/accounts/practitioner-account/profile-settings"
              className="md:hidden"
            >
              <div className="dashboard-card flex-row items-center hover:bg-brand-off cursor-pointer gap-5">
                <div className="flex flex-col gap-2 flex-1">
                  <div className="flex items-center gap-3 text-brand-text">
                    <PiUserCircle className="h-6 w-6" />
                    <span className="font-bold text-lg">Profile settings</span>
                  </div>
                  <span className="text-left font-light text-sm">
                    Update your profile
                  </span>
                </div>
                <div className="h-full flex items-center self-center">
                  <div className="w-10 h-10 bg-brand rounded-full flex items-center justify-center text-white">
                    <PiArrowRight className="h-8 w-8" />
                  </div>
                </div>
              </div>
            </Link>

            <div className="md:col-span-2 flex flex-col gap-6">
              <div className="dashboard-card p-0 divide-y">
                <div className="flex items-center gap-3 p-4 text-brand-text">
                  <VscMegaphone className="h-6 w-6" />
                  <span className="font-bold">Noticeboard</span>
                </div>
                {dashboardData ? (
                  <div className="flex flex-col relative divide-y">
                    {dashboardData.notices
                      .slice(0, initialNoticeDisplayCount)
                      .map((notice, i) => (
                        <NoticeItem
                          notice={notice}
                          key={notice.id}
                          index={i}
                          exitIndex={i}
                        />
                      ))}
                    <AnimatePresence>
                      {expandNotices &&
                        dashboardData.notices
                          .slice(initialNoticeDisplayCount)
                          .map((notice, i) => (
                            <NoticeItem
                              notice={notice}
                              key={notice.id}
                              index={i}
                              exitIndex={
                                dashboardData.notices.length -
                                initialNoticeDisplayCount -
                                i
                              }
                            />
                          ))}
                    </AnimatePresence>
                  </div>
                ) : (
                  <div className="py-2 w-full flex justify-center">
                    <Spinner color="#587500" size="md" />
                  </div>
                )}

                {dashboardData && dashboardData.notices.length > 3 && (
                  <div className="p-5">
                    <Button
                      className="border border-brand bg-white text-brand py-4 rounded-md w-full font-bold transition hover:text-white hover:bg-brand"
                      onClick={() => setExpandNotices(!expandNotices)}
                    >
                      {expandNotices ? "Show less" : "Show more"}
                    </Button>
                  </div>
                )}
              </div>

              <div className="dashboard-card p-0">
                <div className="flex items-center gap-3 p-4 text-brand-text border-b">
                  <PiUserCircle className="h-6 w-6" />
                  <span className="font-bold">Bookmarked therapists</span>
                </div>
                <div className="flex flex-col w-full divide-y">
                  {loadingTherapists ? (
                    <div className="w-full py-4 flex mt-4 items-center justify-center">
                      <Spinner color="#587500" size="xl" />
                    </div>
                  ) : savedTherapists.length === 0 ? (
                    <div className="flex p-4 items-start gap-3 bg-white transition w-full justify-center">
                      <span className="text-brand-alt">
                        You don&apos;t currently have any bookmarked therapists
                      </span>
                    </div>
                  ) : (
                    savedTherapists.map((therapist) => (
                      <div
                        key={therapist.profileId}
                        className="flex p-4 items-start gap-3 bg-white hover:bg-brand-off/50 cursor-pointer transition w-full"
                      >
                        <Link
                          prefetch={false}
                          href={`/counsellors/${therapist.pageName}`}
                          className="flex flex-1 gap-3"
                        >
                          <CustomAvatar
                            name={therapist.fullname}
                            avatarImage={therapist.profileImage}
                          />
                          <div className="flex flex-col flex-1 items-start gap-1.5">
                            <span className="font-bold text-brand-text">
                              {therapist.fullname}
                            </span>
                            <span className="text-left font-light text-sm text-brand-alt">
                              {therapist.therapistTitles
                                .map((title) => title.label)
                                .join(", ")}
                            </span>
                          </div>
                        </Link>
                        <div className="h-full flex items-center self-center">
                          {deletingList.includes(therapist.profileId) ? (
                            <Spinner color="#587500" size="md" />
                          ) : (
                            <Button
                              onClick={(e) => {
                                e.stopPropagation();
                                removeSavedTherapist(therapist.profileId);
                              }}
                              className="w-10 h-10 border border-brand bg-white rounded-full flex items-center justify-center text-brand transition hover:bg-brand hover:text-white"
                            >
                              <FaBookmark className="h-5 w-5 transition" />
                            </Button>
                          )}
                        </div>
                      </div>
                    ))
                  )}
                </div>
                {savedTherapists.length > 0 && (
                  <Link
                    prefetch={false}
                    href="/accounts/common-account/bookmarks"
                    className="p-5"
                  >
                    <Button className="border border-brand bg-white text-brand py-4 rounded-md w-full font-bold transition hover:text-white hover:bg-brand">
                      Show all
                    </Button>
                  </Link>
                )}
              </div>

              <div className="dashboard-card p-0">
                <div className="flex items-center gap-3 p-4 text-brand-text border-b">
                  <TiDocumentText className="h-6 w-6" />
                  <span className="font-bold">Bookmarked articles</span>
                </div>
                <div className="flex flex-col w-full divide-y">
                  {loadingArticles ? (
                    <div className="w-full py-4 flex mt-4 items-center justify-center">
                      <Spinner color="#587500" size="xl" />
                    </div>
                  ) : savedArticles.length === 0 ? (
                    <div className="flex p-4 items-start gap-3 bg-white transition w-full justify-center">
                      <span className="text-brand-alt">
                        You don&apos;t currently have any bookmarked content
                      </span>
                    </div>
                  ) : (
                    savedArticles.map((article) => (
                      <div
                        key={article.uuid}
                        className="flex p-4 items-start gap-3 bg-white hover:bg-brand-off/50 cursor-pointer transition w-full"
                      >
                        <Link
                          prefetch={false}
                          href={`/${article.full_slug}`}
                          className="flex flex-1 gap-3"
                        >
                          <div className="rounded-md overflow-hidden relative w-12 h-12">
                            <Image
                              alt="Article Image"
                              src={article.content.headerImage.filename}
                              fill={true}
                              className="object-cover"
                            />
                          </div>
                          <div className="flex flex-col flex-1 items-start gap-1.5">
                            <span className="font-bold text-brand-text text-left">
                              {article.content.title}
                            </span>
                            <span className="text-left font-light text-sm text-brand-alt">
                              {dayjs(article.date).format("Do MMMM YYYY")}
                            </span>
                          </div>
                        </Link>
                        <div className="h-full flex items-center self-center">
                          {deletingArticleList.includes(article.uuid) ? (
                            <Spinner color="#587500" size="md" />
                          ) : (
                            <Button
                              onClick={(e) => {
                                e.stopPropagation();
                                removeSavedArticle(article.uuid);
                              }}
                              className="w-10 h-10 border border-brand bg-white rounded-full flex items-center justify-center text-brand transition hover:bg-brand hover:text-white"
                            >
                              <FaBookmark className="h-5 w-5 transition" />
                            </Button>
                          )}
                        </div>
                      </div>
                    ))
                  )}
                </div>
                {savedArticles.length > 0 && (
                  <Link
                    prefetch={false}
                    href="/accounts/common-account/bookmarks"
                    className="p-5"
                  >
                    <Button className="border border-brand bg-white text-brand py-4 rounded-md w-full font-bold transition hover:text-white hover:bg-brand">
                      Show all
                    </Button>
                  </Link>
                )}
              </div>

              <div className="dashboard-card p-0 divide-y">
                <div className="flex items-center gap-3 p-4 text-brand-text">
                  <PiCalendarBlank className="h-6 w-6" />
                  <span className="font-bold">
                    CPD hub, peer support and community events
                  </span>
                </div>
                <Link
                  prefetch={false}
                  href="/free-online-cpd-welldoing-members"
                >
                  <div className="flex justify-between w-full p-5 items-center bg-white hover:bg-brand-off/50 cursor-pointer transition">
                    <span className="text-brand-text">
                      Register for upcoming CPD, peer support and other member
                      events, and watch past CPD sessions
                    </span>
                    <div className="h-full flex items-center self-center">
                      <div className="w-6 h-6 bg-brand rounded-full flex items-center justify-center text-white">
                        <PiArrowRight className="h-5 w-5" />
                      </div>
                    </div>
                  </div>
                </Link>
              </div>

              <div
                className={`dashboard-card p-0 divide-y ${dashboardData ? "min-h-[450px]" : ""}`}
              >
                <div className="flex items-center gap-3 p-4 text-brand-text">
                  <MdInsertChartOutlined className="h-6 w-6" />
                  <span className="font-bold">Profile stats</span>
                </div>
                {dashboardData ? (
                  <div className="flex flex-col w-full flex-1">
                    <div className="flex items-center gap-4 p-5 pb-0">
                      <div className="flex items-center gap-2">
                        <button
                          type="button"
                          onClick={() => toggleDataView("search")}
                          className={`${dataView === "search" ? "bg-brand-alt text-white rounded-full px-2 py-1" : "text-muted"} text-brand-text font-bold`}
                        >
                          Search results
                        </button>
                      </div>
                      <Switch
                        size="lg"
                        colorScheme="primary"
                        sx={{
                          ".chakra-switch__track:not([data-checked])": {
                            bg: "brand.200",
                          }, // brand-alt
                        }}
                        isChecked={dataView === "profile_view"}
                        onChange={(e) =>
                          toggleDataView(
                            e.target.checked ? "profile_view" : "search",
                          )
                        }
                      />
                      <div className="flex items-center gap-2">
                        <button
                          type="button"
                          onClick={() => toggleDataView("profile_view")}
                          className={`${dataView === "profile_view" ? "bg-brand text-white rounded-full px-2 py-1" : "text-brand-text"} text-brand-text font-bold`}
                        >
                          Profile views
                        </button>
                      </div>
                    </div>
                    <ResponsiveContainer
                      width="100%"
                      height="100%"
                      className="flex-1"
                    >
                      <ComposedChart
                        width={500}
                        height={400}
                        data={chartData}
                        margin={{
                          top: 20,
                          right: 20,
                          bottom: 20,
                          left: 0,
                        }}
                      >
                        <XAxis
                          fill="#9F9F9F"
                          padding={{ left: 20, right: 20 }}
                          tickLine={false}
                          axisLine={false}
                          dataKey="name"
                        />
                        <YAxis
                          padding={{ top: 20, bottom: 20 }}
                          tickLine={false}
                          axisLine={false}
                        />
                        <Tooltip
                          content={(props) => (
                            <ViewsChartTooltip {...props} type={dataView} />
                          )}
                        />
                        <Line
                          isAnimationActive={false}
                          dot={false}
                          type="monotone"
                          dataKey={dataView}
                          stroke={
                            dataView === "profile_view" ? "#587500" : "#3F3B39"
                          }
                          strokeDasharray="5 5"
                        />
                      </ComposedChart>
                    </ResponsiveContainer>
                  </div>
                ) : (
                  <div className="flex w-full py-4 items-center justify-center">
                    <Spinner size="xl" color="#587500" />
                  </div>
                )}
              </div>
            </div>

            <div className="flex flex-col gap-6">
              <Link
                prefetch={false}
                href="/accounts/practitioner-account/profile-settings"
                className="hidden md:block"
              >
                <div className="dashboard-card flex-row items-center hover:bg-brand-off cursor-pointer gap-5">
                  <div className="flex flex-col gap-2 flex-1">
                    <div className="flex items-center gap-3 text-brand-text">
                      <PiUserCircle className="h-6 w-6" />
                      <span className="font-bold text-lg text-left">
                        Profile settings
                      </span>
                    </div>
                    <span className="text-left font-light text-sm">
                      Update your profile
                    </span>
                  </div>
                  <div className="h-full flex items-center self-center">
                    <div className="w-10 h-10 bg-brand rounded-full flex items-center justify-center text-white">
                      <PiArrowRight className="h-8 w-8" />
                    </div>
                  </div>
                </div>
              </Link>
              <Link
                prefetch={false}
                href="/accounts/common-account/account-settings"
                className="hidden md:block"
              >
                <div className="dashboard-card flex-row items-center hover:bg-brand-off cursor-pointer gap-5">
                  <div className="flex flex-col gap-2 flex-1">
                    <div className="flex items-center gap-3 text-brand-text">
                      <PiGear className="h-6 w-6" />
                      <span className="font-bold text-lg text-left">
                        Account settings
                      </span>
                    </div>
                    <span className="text-left font-light text-sm">
                      Manage your account and payments
                    </span>
                  </div>
                  <div className="h-full flex items-center self-center">
                    <div className="w-10 h-10 bg-brand rounded-full flex items-center justify-center text-white">
                      <PiArrowRight className="h-8 w-8" />
                    </div>
                  </div>
                </div>
              </Link>
              <Button
                onClick={() => setViewContact(!viewingContact)}
                className="dashboard-card hidden md:flex flex-row items-center hover:bg-brand-off cursor-pointer gap-5"
              >
                <div className="flex flex-col gap-2 flex-1">
                  <div className="flex items-center gap-3 text-brand-text">
                    <PiEnvelopeSimple className="h-6 w-6" />
                    <span className="font-bold text-lg text-left">
                      Contact Welldoing
                    </span>
                  </div>
                  {viewingContact ? (
                    <div className="flex flex-col divide-y">
                      <div className="flex items-center gap-2 py-2">
                        <PiEnvelopeSimple className="h-4 w-4" />
                        <span className="font-bold text-brand-text">
                          Email:{" "}
                        </span>
                        <Link
                          prefetch={false}
                          onClick={(e) => e.stopPropagation()}
                          href="mailto:info@welldoing.org"
                        >
                          <span className="text-brand underline">
                            info@welldoing.org
                          </span>
                        </Link>
                      </div>
                    </div>
                  ) : (
                    <span className="text-left font-light text-sm">
                      Welldoing is here to support you every step of the way.
                    </span>
                  )}
                </div>
                <div
                  className={`h-full flex items-center ${viewingContact ? "hidden" : ""}`}
                >
                  <div className="w-10 h-10 bg-brand rounded-full flex items-center justify-center text-white">
                    <PiArrowRight className="h-8 w-8" />
                  </div>
                </div>
              </Button>
            </div>
          </div>
        </div>
      </div>
      <UserRegisterSuccessModal
        isTherapist
        isSuccessOpen={isSuccessOpen}
        onSuccessClose={onSuccessClose}
      />
      <ChangeDashboardViewModal
        isOpen={changingHome}
        onClose={closeChangingHome}
        page="/"
      />
    </div>
  );
};

export default withAuth(TherapistDashboard);
